import { filter, find, isArray, map, reduce, sortBy, uniq } from 'lodash';

import {
  AutomaticCondition,
  AutomaticConditionAttributesTable,
  AutomaticConditionLogical,
  CellFiltersTypes,
  ConditionOperator,
  ConditionType,
} from 'interfaces/automaticCondition';

export interface CellPopulationGroupState {
  id: string;
  name?: string;
  featureSelectionKey?: string;
  cellClassIds: string[];
  cellAttributeIds: string[];
  attributeConditions: AutomaticCondition[];
}

export interface CellPopulationFeatureFilter {
  featureSelectionKey: string;
  condition: AutomaticCondition;
}

export interface PopulationGroupNewCell {
  name: string;
  value: AutomaticCondition | null;
  stain: string;
  populationGroup?: CellPopulationGroupState;
}

interface RestorablePopulationCondition {
  cellClassIds: string[];
  attributeConditions: AutomaticCondition[];
}

const getRestorablePopulationCondition = (condition: AutomaticCondition): RestorablePopulationCondition | undefined => {
  const logicalCondition = condition as AutomaticConditionLogical;
  if (logicalCondition.operator !== ConditionOperator.AND || !isArray(logicalCondition.operands)) {
    return undefined;
  }

  const cellClassCondition = find(
    logicalCondition.operands,
    (operand) =>
      (operand as AutomaticConditionAttributesTable).mappingFilter === 'cell.label' &&
      (operand as AutomaticConditionAttributesTable).filterAction === CellFiltersTypes.filter_in &&
      isArray((operand as AutomaticConditionAttributesTable).toFilter)
  ) as AutomaticConditionAttributesTable | undefined;
  if (!cellClassCondition) {
    return undefined;
  }

  return {
    cellClassIds: sortBy(map(cellClassCondition.toFilter as string[], (value) => `${value}`)),
    attributeConditions: filter(logicalCondition.operands, (operand) => operand !== cellClassCondition),
  };
};

const getCompatibleGroupKey = ({
  featureSelectionKey,
  attributeConditions,
}: Pick<CellPopulationGroupState, 'featureSelectionKey' | 'attributeConditions'>) =>
  `${featureSelectionKey}:${JSON.stringify(attributeConditions)}`;

export const cellPopulationGroupToCondition = (group: CellPopulationGroupState): AutomaticCondition | null => {
  const operands: AutomaticCondition[] = [...group.attributeConditions];
  if (group.cellClassIds.length) {
    operands.unshift({
      conditionType: ConditionType.ATTRIBUTES_TABLE,
      mappingFilter: 'cell.label',
      filterAction: CellFiltersTypes.filter_in,
      toFilter: group.cellClassIds,
      displayValue: group.name,
    });
  }

  return operands.length ? { operator: ConditionOperator.AND, operands } : null;
};

export const updateCellPopulationGroupsInNewCells = (
  newCells: Record<string, PopulationGroupNewCell>,
  groups: CellPopulationGroupState[],
  stain: string
): Record<string, PopulationGroupNewCell> => {
  const groupIds = new Set(groups.map(({ id }) => id));
  const updatedNewCells = { ...newCells };

  Object.entries(updatedNewCells).forEach(([id, cell]) => {
    if (cell.stain === stain && cell.populationGroup && !groupIds.has(id)) {
      delete updatedNewCells[id];
    }
  });

  groups.forEach((group) => {
    updatedNewCells[group.id] = {
      name: group.name || 'Cell Population',
      value: cellPopulationGroupToCondition(group),
      stain,
      populationGroup: group,
    };
  });

  return updatedNewCells;
};

export const restoreCellPopulationGroups = (
  featureFilters: CellPopulationFeatureFilter[]
): CellPopulationGroupState[] =>
  reduce(
    featureFilters,
    (groups, { featureSelectionKey, condition }, index) => {
      const restoredCondition = getRestorablePopulationCondition(condition);
      if (!restoredCondition) {
        return groups;
      }

      const groupKey = getCompatibleGroupKey({ featureSelectionKey, ...restoredCondition });
      const existingGroup = find(groups, (group) => getCompatibleGroupKey(group) === groupKey);
      if (existingGroup) {
        existingGroup.cellClassIds = sortBy(uniq([...existingGroup.cellClassIds, ...restoredCondition.cellClassIds]));
        return groups;
      }

      return [
        ...groups,
        {
          id: `restored-population:${featureSelectionKey}:${index}`,
          featureSelectionKey,
          cellClassIds: restoredCondition.cellClassIds,
          cellAttributeIds: map(
            restoredCondition.attributeConditions,
            (attributeCondition) => `${(attributeCondition as AutomaticConditionAttributesTable).mappingFilter || ''}`
          ),
          attributeConditions: restoredCondition.attributeConditions,
        },
      ];
    },
    [] as CellPopulationGroupState[]
  );
